<template>
  <tr
    class="rf-row group/row hover:bg-beige-0 transition-background relative duration-300 ease-in-out"
    :class="{ 'bg-beige-10!': selected }"
  >
    <td class="pr-0! whitespace-nowrap" style="min-width: calc(24px + 1rem)">
      <RfCheckbox
        data-cy="guide-row--checkbox"
        :data-cy-value="selected"
        class="rf-row--checkbox py-1 opacity-0 group-hover/row:opacity-100!"
        :class="{ 'opacity-100': selected }"
        :modelValue="selected"
        @update:modelValue="$emit(selected ? 'deselect' : 'select', guide.id)"
      />
    </td>
    <td class="5xl:px-7! px-5! whitespace-nowrap">
      <span class="flex items-center justify-center gap-0.5">
        <RfMenu
          preset="promptStatus"
          class="5xl:justify-start! justify-center!"
          :disabled="disabled || getGuideStatus.type === SimplifyStatusesMapConstants.limited.type"
        >
          <template #activator>
            <span class="inline-flex items-center gap-2">
              <RfStatus :status="getGuideStatus.type" />
              <span class="text-body 5xl:inline-block text-grey-1 hidden">
                {{ StatusesConstants[getGuideStatus.type].title }}
              </span>
            </span>
          </template>
          <template #menu="{ close }">
            <template v-for="status in StatusesSimplifiedConstants" :key="status.type">
              <button
                v-if="
                  status.type === SimplifyStatusesMapConstants.ready.type
                    ? !guide.is_enabled && dayjs(guide.start_date).isAfter(dayjs())
                    : ![
                        SimplifyStatusesMapConstants[getGuideStatus.type].type,
                        SimplifyStatusesMapConstants.limited.type,
                      ].includes(status.type)
                "
                class="flex w-full items-center gap-3 px-4 py-2 first:pt-4! last:pb-4!"
                @click="
                  $emit('status', status.type);
                  close();
                "
              >
                <RfStatus :status="status.type" />
                {{ status.action }}
              </button>
            </template>
          </template>
        </RfMenu>
      </span>
    </td>
    <td class="pl-0!">
      <div class="5xl:gap-7 flex items-center gap-5">
        <span
          ref="thumbnailRef"
          class="rf-thumbnail relative mr-1 block h-24 w-32 shrink-0"
          :class="{ invisible: isPreviewVisible }"
        >
          <div class="h-full w-full overflow-hidden rounded">
            <RfPromotionThumbnail
              v-if="guide.paths.length"
              :key="guide.id"
              :item="guide.paths[0]"
              class="[&_img]:h-full [&_img]:w-full [&_img]:object-cover"
            />
            <div v-else class="bg-icon flex h-full w-full">
              <span class="text-white-1 m-auto text-xs font-bold uppercase">No prompts yet</span>
            </div>
            <button
              v-if="guide.paths.length"
              class="rf-thumbnail--overlay text-body text-white-1 absolute inset-0 flex rounded opacity-0 hover:opacity-100!"
              @click="openPreview"
            >
              <span class="m-auto flex items-center gap-1">
                <RfEyeOpenIcon class="fill-white-1 h-6! w-6!" />
                Preview
              </span>
            </button>
          </div>
          <span
            v-if="!isPreviewVisible"
            aria-hidden="true"
            class="text-body bg-grey-1 text-white-1 absolute -right-1 -bottom-1 z-0 grid h-6 w-6 place-content-center rounded-full p-1"
          >
            {{ guide.paths.length }}
          </span>
        </span>
        <Teleport to="body">
          <span
            v-if="isPreviewVisible"
            ref="floatingRef"
            class="rf-floating-preview"
            :class="{ 'rf-floating-preview--expanded': isExpanded }"
            :style="floatingStyle"
            @click="closePreview"
          >
            <RfPromotionThumbnail
              :key="guide.id"
              :item="guide.paths[0]"
              class="[&_img]:h-full [&_img]:w-full [&_img]:object-cover"
            />
          </span>
        </Teleport>
        <span class="flex flex-col gap-1">
          <div class="flex items-start gap-2">
            <RouterLink
              class="text-action-buttons line-clamp-1 font-bold! text-ellipsis text-blue-50"
              :title="guide.name"
              :to="getGuideLink(guide.id, $route.params.aid)"
            >
              {{ guide.name }}
            </RouterLink>
            <button
              v-if="!disabled"
              class="flex items-center gap-3 opacity-0 transition-opacity duration-300 ease-in-out group-hover/row:opacity-100!"
              @click="$emit('edit')"
            >
              <RfEditIcon class="fill-beige-50 h-5! w-5!" />
            </button>
          </div>
          <span class="text-body text-grey-1">
            Show prompts: {{ GUIDE_TYPES[guide.sequence_type].label || GUIDE_TYPES.survey.label }}
          </span>
          <span class="text-body text-grey-1 inline-flex gap-1"
            ><span>Creator:</span>
            <component
              :is="guide.creator?.id ? 'button' : 'span'"
              class="inline-flex text-start text-blue-50!"
              @click="
                guide.creator?.id &&
                $emit('filterUser', guide.creator.id, getUserName(guide.creator))
              "
            >
              {{ getUserName(guide.creator) }}
            </component>
          </span>
        </span>
      </div>
    </td>
    <td class="w-0">
      <div
        class="rf-row--segment-wrapper text-body text-grey-1 4xl:w-40 ml-0! flex w-24 flex-col break-keep *:truncate *:break-keep"
      >
        <template v-if="guide.segments.length">
          <RouterLink
            v-for="segment in [...guide.segments].sort((a, b) =>
              `${a.name}`.localeCompare(`${b.name}`),
            )"
            :key="segment.id"
            :title="segment.name"
            class="text-blue-50!"
            :to="getSegmentLink(segment.id, $route.params.aid)"
          >
            {{ segment.name }}
          </RouterLink>
        </template>
        <span v-else>No segments</span>
      </div>
    </td>
    <td v-if="engagementCategoriesEnabled" class="w-0">
      <div class="text-body text-grey-1 4xl:w-32 flex w-24 flex-col items-start gap-2">
        <RfColorTag
          v-if="guide.engagement_category"
          :backgroundColor="getCategoryColor(guide.engagement_category)"
          :title="getCategoryLabel(guide.engagement_category)"
          pill
          disabled
          class="p-0! *:py-1!"
        />
        <span v-else class="text-body text-beige-50">None</span>
      </div>
    </td>
    <td class="px-4! whitespace-nowrap">
      <span class="text-body text-black-2">
        {{ dateDisplay(guide.start_date, { utc: true, alter: true }) }}
      </span>
    </td>
    <td class="relative px-4! whitespace-nowrap">
      <span class="text-body text-black-2">
        {{ dateDisplay(guide.end_date, { utc: true, alter: true }) || "∞" }}
        <RfPromptDateTag
          class="absolute top-1/2 right-2 mt-3 box-content"
          :date="new Date(guide.end_date)"
          :isExpired="guide.is_expired"
          :isLimited="getGuideStatus.type === StatusesSimplifiedConstants.limited.type"
        />
      </span>
    </td>
    <td
      v-for="(colVal, index) in [currUsers, currGoals]"
      :key="index"
      class="rf-row--metric-col text-body bg-goal text-black-2 px-4 text-right whitespace-nowrap"
    >
      <RfTooltip v-if="!colVal" class="mr-0! w-fit">
        <template #activator> <RfHelpIcon class="h-6! w-6!" /> </template>
        <template #tooltip>
          <div class="w-64">{{ emptyDataText }}</div>
        </template>
      </RfTooltip>
      <span v-else class="text-body text-decoration-none text-black-2">{{
        toLocaleNumberString(colVal)
      }}</span>
    </td>
    <td class="rf-row--metric-col text-body bg-goal text-black-2 px-4 text-right whitespace-nowrap">
      <RfTooltip v-if="!currConversions.value" class="mr-0! w-fit">
        <template #activator> <RfHelpIcon class="h-6! w-6!" /> </template>
        <template #tooltip>
          <div class="w-64">{{ emptyDataText }}</div>
        </template>
      </RfTooltip>
      <span v-else>{{ currConversions.label }}</span>
    </td>
    <td class="px-2! whitespace-nowrap">
      <div class="flex items-center justify-center">
        <RfMenu v-if="!disabled" hideIcon preset="promptMenu">
          <template #activator="{ opened }">
            <RfDotsIcon
              class="fill-beige-50 m-auto flex h-6! w-6! opacity-0 transition-opacity duration-300 ease-in-out group-hover/row:opacity-100!"
              :class="{ 'opacity-100!': opened }"
            />
          </template>
          <template #menu="{ close }">
            <button
              class="flex w-full items-center gap-3 px-4 py-2 pt-4 first:pt-4!"
              @click="
                $emit('edit');
                close();
              "
            >
              <RfEditIcon class="fill-beige-50 h-6! w-6!" />
              Edit
            </button>
            <button
              class="flex w-full items-center gap-3 px-4 py-2"
              @click="
                $emit('clone');
                close();
              "
            >
              <RfCloneIcon class="fill-beige-50 h-6! w-6!" />
              Clone
            </button>
            <button
              class="flex w-full items-center gap-3 px-4 py-2"
              @click="
                exportMultiplePromptToCsv(guide.paths, timePeriod);
                close();
              "
            >
              <RfDownloadIcon class="fill-beige-50 h-6! w-6!" />
              Export All Prompts to CSV
            </button>
            <button
              class="flex w-full items-center gap-3 px-4 py-2 pb-4 last:pb-4!"
              @click="
                $emit('delete');
                close();
              "
            >
              <RfDeleteIcon class="fill-beige-50 h-6! w-6!" />
              Delete
            </button>
          </template>
        </RfMenu>
      </div>
    </td>
  </tr>
</template>

<script>
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import getPromptStatus from "@/utils/prompts/getPromptStatus";
import RfMenu from "@/components/menus/RfMenu.vue";
import StatusesConstants, {
  StatusesSimplifiedConstants,
  SimplifyStatusesMapConstants,
} from "@/utils/constants/PromptStatusesConstants";
import RfPromotionThumbnail from "@/components/RfPromotions/RfPromotionThumbnail.vue";
import RfDotsIcon from "@/components/icons/RfDotsIcon.vue";
import { getSegmentLink, getGuideLink } from "@/utils/getLink";
import { tally, percentage, statsAggregator } from "@/utils/metricsHelpers";
import RfEditIcon from "@/components/icons/RfEditIcon.vue";
import RfCloneIcon from "@/components/icons/RfCloneIcon.vue";
import RfHelpIcon from "@/components/icons/RfHelpIcon.vue";
import RfDeleteIcon from "@/components/icons/RfDeleteIcon.vue";
import RfPromptDateTag from "@/components/tags/RfPromptDateTag.vue";
import RfTooltip from "@/components/tooltip/RfTooltip.vue";
import RfEyeOpenIcon from "@/components/icons/RfEyeOpenIcon.vue";
import dayjs from "dayjs";
import { GUIDE_TYPES } from "@/utils/constants/GuidesConstants";
import RfStatus from "@/blocks/RfPrompts/RfStatus.vue";
import RfDownloadIcon from "@/components/icons/RfDownloadIcon.vue";
import { getUserName, toLocaleNumberString } from "@/utils/stringHelpers";
import { useFloatingPreview } from "@/utils/composables/useFloatingPreview";
import { dateDisplay } from "@/utils/DateDisplayUtils";
import { RouterLink } from "vue-router";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import { getCategoryColor, getCategoryLabel } from "@/utils/constants/EngagementCategoryConstants";
import { exportMultiplePromptToCsv } from "@/utils/CsvUtils";

const emptyDataText =
  "There is no data for this period of time. Try selecting a different time range or make sure the guide has been active.";

export default {
  name: "RfGuidesRow",
  components: {
    RfCheckbox,
    RfMenu,
    RfPromotionThumbnail,
    RfDotsIcon,
    RfEditIcon,
    RfCloneIcon,
    RfDeleteIcon,
    RfPromptDateTag,
    RfHelpIcon,
    RfTooltip,
    RfEyeOpenIcon,
    RfStatus,
    RfDownloadIcon,
    RouterLink,
    RfColorTag,
  },
  props: {
    guide: { type: Object, default: () => ({}) },
    selected: { type: Boolean, default: false },
    timezone: { type: String, default: null },
    timePeriod: { type: String, default: null },
    disabled: { type: [Boolean, String], default: false },
    engagementCategoriesEnabled: { type: Boolean, default: false },
  },
  setup: () => {
    const {
      thumbnailRef,
      floatingRef,
      isPreviewVisible,
      isExpanded,
      floatingStyle,
      openPreview,
      closePreview,
    } = useFloatingPreview();

    return {
      StatusesSimplifiedConstants,
      StatusesConstants,
      SimplifyStatusesMapConstants,
      getSegmentLink,
      getGuideLink,
      emptyDataText,
      dayjs,
      GUIDE_TYPES,
      toLocaleNumberString,
      thumbnailRef,
      floatingRef,
      isPreviewVisible,
      isExpanded,
      floatingStyle,
      openPreview,
      closePreview,
      dateDisplay,
      getUserName,
      getCategoryColor,
      getCategoryLabel,
      exportMultiplePromptToCsv,
    };
  },
  computed: {
    data() {
      return statsAggregator(this.guide.paths, this.timePeriod);
    },
    getGuideStatus() {
      return getPromptStatus(this.guide, this.timezone);
    },
    currUsers() {
      return tally(this.data, this.timePeriod, "uimpressions");
    },
    currClicks() {
      return tally(this.data, this.timePeriod, "goals");
    },
    currGoals() {
      return tally(this.data, this.timePeriod, "goals");
    },
    currConversions() {
      return this.currUsers ? percentage(this.currClicks, this.currUsers, "new") : { value: 0 };
    },
  },
};
</script>

<style lang="scss" scoped>
.rf-thumbnail {
  .promo-preview-image {
    min-width: auto;
    height: 100%;
  }

  &--overlay {
    transition: opacity 300ms ease-in-out;

    &:hover {
      background: rgb(36 36 36 / 0.6);
    }
  }
}

.rf-floating-preview {
  display: block;
  cursor: pointer;
  transform: scale(1);
  transform-origin: center top;
  transition: transform 300ms cubic-bezier(0.5, 1.2, 0.5, 1.2);

  &--expanded {
    transform: scale(2.5);
  }

  .promo-preview-image {
    min-width: auto;
    width: 100%;
    height: 100%;
  }
}
</style>
